import React, { useState, useEffect } from 'react';
import { Key, Trash2, Plus, Shield, Smartphone, Monitor, Loader2 } from 'lucide-react';
import { PasskeysService, PasskeyCredential } from '../services/passkeysService';
import { toast } from 'react-hot-toast';

interface PasskeysManagerProps {
  userId: string;
}

export const PasskeysManager: React.FC<PasskeysManagerProps> = ({ userId }) => {
  const [passkeys, setPasskeys] = useState<PasskeyCredential[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [isPlatformAvailable, setIsPlatformAvailable] = useState(false);

  useEffect(() => {
    checkSupport();
    loadPasskeys();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkSupport = async () => {
    const supported = PasskeysService.isSupported();
    setIsSupported(supported);
    
    if (supported) {
      const platformAvailable = await PasskeysService.isPlatformAuthenticatorAvailable();
      setIsPlatformAvailable(platformAvailable);
    }
  };

  const loadPasskeys = async () => {
    try {
      setIsLoading(true);
      const userPasskeys = await PasskeysService.getUserPasskeys(userId);
      setPasskeys(userPasskeys);
    } catch (error) {
      console.error('Failed to load passkeys:', error);
      toast.error('Failed to load passkeys');
    } finally {
      setIsLoading(false);
    }
  };

  const registerPasskey = async () => {
    try {
      setIsRegistering(true);
      const result = await PasskeysService.registerPasskey(userId);
      
      if (result.success) {
        toast.success('Passkey registered successfully!');
        loadPasskeys(); // Reload the list
      } else {
        toast.error(result.error || 'Failed to register passkey');
      }
    } catch (error) {
      console.error('Failed to register passkey:', error);
      toast.error('Failed to register passkey');
    } finally {
      setIsRegistering(false);
    }
  };

  const deletePasskey = async (credentialId: string) => {
    if (!window.confirm('Are you sure you want to delete this passkey?')) {
      return;
    }

    try {
      const result = await PasskeysService.deletePasskey(credentialId);
      
      if (result.success) {
        toast.success('Passkey deleted successfully');
        loadPasskeys(); // Reload the list
      } else {
        toast.error(result.error || 'Failed to delete passkey');
      }
    } catch (error) {
      console.error('Failed to delete passkey:', error);
      toast.error('Failed to delete passkey');
    }
  };

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType.toLowerCase()) {
      case 'ios':
      case 'android':
        return <Smartphone className="w-4 h-4" />;
      case 'macos':
      case 'windows':
      case 'linux':
        return <Monitor className="w-4 h-4" />;
      default:
        return <Key className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (!isSupported) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 text-yellow-800">
          <Shield className="w-5 h-5" />
          <span className="font-medium">Passkeys Not Supported</span>
        </div>
        <p className="text-yellow-700 text-sm mt-1">
          Your browser doesn't support passkeys. Please use a modern browser like Chrome, Safari, or Edge.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Passkeys</h3>
        <p className="text-gray-600 text-sm">
          Passkeys provide secure, passwordless authentication using your device's built-in security features.
        </p>
      </div>

      {/* Register New Passkey */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Add New Passkey</h4>
        
        <div className="space-y-3">
          <button
            onClick={registerPasskey}
            disabled={isRegistering || !isPlatformAvailable}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRegistering ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            <span>{isRegistering ? 'Creating...' : 'Add Passkey'}</span>
          </button>
          
          {!isPlatformAvailable && (
            <p className="text-sm text-amber-600">
              Platform authenticator not available. You may need to enable biometric authentication on your device.
            </p>
          )}
        </div>
      </div>

      {/* Existing Passkeys */}
      <div>
        <h4 className="font-medium text-gray-900 mb-3">Your Passkeys</h4>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-600">Loading passkeys...</span>
          </div>
        ) : passkeys.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Key className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No passkeys registered yet</p>
            <p className="text-sm">Add your first passkey above to get started</p>
          </div>
        ) : (
          <div className="space-y-3">
            {passkeys.map((passkey) => (
              <div
                key={passkey.id}
                className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  {getDeviceIcon(passkey.device_type)}
                  <div>
                    <p className="font-medium text-gray-900">{passkey.name}</p>
                    <p className="text-sm text-gray-500">
                      {passkey.device_type} • Added {formatDate(passkey.created_at)}
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={() => deletePasskey(passkey.credential_id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete passkey"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Security Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <Shield className="w-5 h-5 text-gray-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-gray-900">Why Use Passkeys?</h4>
            <ul className="text-gray-700 text-sm mt-1 space-y-1">
              <li>• More secure than passwords</li>
              <li>• No passwords to remember or type</li>
              <li>• Protected by your device's biometric authentication</li>
              <li>• Resistant to phishing attacks</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
